import { fetchMarketBanks } from '../../../../api/account';
import { fetchBrokerBanks, fetchStockAccountsMeta, fetchUserBanks } from '../../../../api/funds';
import { getUserInfo, isH5User, userState } from '../../../../stores/user';
import { gotoLogin } from '../../utils/gotoLogin';
import { bootDepositApp } from './appBridge';
import {
  activeUserBanks,
  is2goNotLoggedIn,
  overviewAccountsOf,
  setMarketBankCache,
  sortDepositAccounts,
  unwrapList,
  type Currency,
} from './shared';

export type DepositBanks = {
  userBanks: any[];
  brokerBanks: any[];
  marketBanks: any[];
};

let accounts: any[] | null = null;
let accountsPromise: Promise<any[]> | null = null;
const bankCache = new Map<Currency, DepositBanks>();
const bankPromises = new Map<Currency, Promise<DepositBanks>>();

const fetchAccounts = async () => {
  await bootDepositApp();
  if (!userState.userInfo) {
    try {
      await getUserInfo();
      console.log('[h5-deposit] userInfo', userState.userInfo);
    } catch (error) {
      console.log('[h5-deposit] getUserInfo failed', error);
      // 没有户号也继续拉总览
    }
  } else {
    console.log('[h5-deposit] userInfo (cached)', userState.userInfo);
  }
  try {
    const list = sortDepositAccounts(overviewAccountsOf(await fetchStockAccountsMeta()));
    accounts = list;
    console.log('[h5-deposit] stock accounts', list);
    return list;
  } catch (error) {
    console.log('[h5-deposit] stock accounts failed', error);
    if (is2goNotLoggedIn(error) && isH5User()) gotoLogin();
    if (accounts) return accounts;
    throw error;
  }
};

/** 账户列表只拉一次。失败不写入缓存，下次还可再试 */
export const ensureDepositAccounts = () => {
  if (accounts) return Promise.resolve(accounts);
  if (!accountsPromise) {
    accountsPromise = fetchAccounts().finally(() => {
      accountsPromise = null;
    });
  }
  return accountsPromise;
};

const fetchBanks = async (currency: Currency): Promise<DepositBanks> => {
  await bootDepositApp();
  const one = async (task: Promise<any>) => {
    try {
      return { ok: true, list: unwrapList(await task) };
    } catch {
      return { ok: false, list: [] as any[] };
    }
  };
  const [users, brokers, markets] = await Promise.all([
    one(fetchUserBanks(currency)),
    one(fetchBrokerBanks(currency)),
    one(fetchMarketBanks(currency)),
  ]);
  const pack: DepositBanks = {
    userBanks: activeUserBanks(users.list),
    brokerBanks: brokers.list,
    marketBanks: markets.list,
  };
  if (users.ok || brokers.ok || markets.ok) {
    bankCache.set(currency, pack);
    setMarketBankCache(markets.list);
  }
  return pack;
};

/** 按币种拉用户卡、券商收款行、市场银行。同币种进行中的请求会合并 */
export const ensureDepositBanks = (currency: Currency) => {
  const hit = bankCache.get(currency);
  if (hit) return Promise.resolve(hit);
  let task = bankPromises.get(currency);
  if (!task) {
    task = fetchBanks(currency).finally(() => {
      bankPromises.delete(currency);
    });
    bankPromises.set(currency, task);
  }
  return task;
};

/** 进页就拉：账户和当前币种银行同时发，互不等待 */
export const warmupDeposit = (currency: Currency) => {
  console.log('[h5-deposit] warmup', currency);
  void ensureDepositAccounts().catch((error) => {
    console.warn('[h5-deposit] ensureDepositAccounts failed', error);
  });
  void ensureDepositBanks(currency).catch((error) => {
    console.warn('[h5-deposit] ensureDepositBanks failed', error);
  });
};
